"use client"

/**
 * The review rail from the v4 homepage (Figma 329:15822), shared so every
 * main page on the membership-first site shows its testimonials the same way.
 *
 *   video card    273×436, r16, poster under a 70%→20%@50%→0 scrim, a 64px
 *                 white/5 circle with the play mark, handle 16/22.5 Medium
 *                 and followers 14/19.5 white/60 in a 16px-padded foot
 *   text card     300×436, r16, neutral-50, 32/29 inset, headline Medium
 *                 20/24.75, body Regular 18/24, then avatar 40 + name 18/24
 *                 + 14px stars, 26 under the copy
 *   CTA           "All reviews", the DS outline pill, centred
 *   arrows        two 40px circles at the right edge, neutral-50/100
 *
 * Copy comes in as props: the homepage passes its own, other pages their
 * heading and the shared "view all reviews" label. Cards come from
 * `reviewCards()` in `lib/review-cards.ts`.
 */

import { useEffect, useRef, useState } from "react"
import Image from "next/image"
import Link from "next/link"
import { buttonVariants } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import {
  StarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@/components/ui/icons"
import type { ReviewCard } from "@/lib/review-cards"

/** The card is a fixed 273×436 (`h-[436px] w-[273px]` below). */
const CARD_SIZES = "273px"

/** The headline is 20/24.75 Medium in the file — between title-md and
 *  title-lg, and Eugene set it deliberately, so it stays rail-scoped. */
export function ReviewsType() {
  return (
    <style>{`
      .hv3-rev-headline{font-size:20px;line-height:24.75px;font-weight:500}
      .hv3-rev-handle{font-size:16px;line-height:22.5px;font-weight:500}
      .hv3-rev-followers{font-size:14px;line-height:19.5px;font-weight:400}
    `}</style>
  )
}

function VideoCard({
  review,
  followersSuffix,
}: {
  review: Extract<ReviewCard, { kind: "video" }>
  followersSuffix: string
}) {
  const ref = useRef<HTMLLIElement>(null)
  const fullRef = useRef<HTMLVideoElement>(null)
  const posterRef = useRef<HTMLImageElement>(null)
  const [visible, setVisible] = useState(false)
  const [playing, setPlaying] = useState(false)
  const [fullPoster, setFullPoster] = useState<string>()

  // The muted preview loop only starts once the card is on screen, the way
  // the site's own Instagram card does — six autoplaying videos below the
  // fold is bandwidth nobody asked for.
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true)
          io.unobserve(el)
        }
      },
      { threshold: 0.3 }
    )
    io.observe(el)
    return () => io.disconnect()
  }, [])

  const playFull = () => {
    if (!review.full) return
    // The native controls paint an opaque box until the first frame; the
    // variant the card's `<Image>` already loaded keeps the photo under the
    // spinner without a new request.
    setFullPoster(posterRef.current?.currentSrc)
    setPlaying(true)
    setTimeout(() => {
      const vid = fullRef.current
      if (!vid) return
      vid.currentTime = 0
      vid.muted = false
      vid.play().catch(() => {
        vid.muted = true
        vid.play().catch(() => {})
      })
    }, 50)
  }

  const name = review.handle ? `@${review.handle}` : review.author
  const sub = review.handle
    ? review.followers
      ? `${review.followers} ${followersSuffix}`
      : ""
    : (review.authorTitle ?? "")

  return (
    <li
      ref={ref}
      className="relative h-[436px] w-[273px] shrink-0 overflow-hidden rounded-2xl bg-neutral-200"
    >
      {/* The poster sits under both videos instead of being the preview's
          `poster`: a `poster` URL has no srcset, so at 3x DPR it fetched a
          second variant next to this one. The muted preview paints nothing
          until its first frame, so this shows through until then. */}
      <Image
        ref={posterRef}
        src={review.poster}
        alt=""
        fill
        sizes={CARD_SIZES}
        className="object-cover"
      />
      {playing && review.full ? (
        <video
          ref={fullRef}
          src={review.full}
          poster={fullPoster}
          controls
          playsInline
          className="absolute inset-0 h-full w-full object-cover"
        />
      ) : (
        <>
          {visible && review.preview ? (
            <video
              src={review.preview}
              muted
              autoPlay
              loop
              playsInline
              className="absolute inset-0 h-full w-full object-cover"
            />
          ) : null}
          {/* Figma 329:15883's third paint: black 70% at the foot → 20% at
              the middle → clear at the top. */}
          <div
            className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 via-50% to-transparent"
            aria-hidden
          />
          <button
            type="button"
            onClick={playFull}
            aria-label={`Play ${name}`}
            className="absolute inset-0 flex cursor-pointer items-center justify-center"
          >
            {/* 64px circle at white 5% over a strong blur of the frame. The
                triangle has rounded corners (a round-joined stroke on the
                filled shape) and sits 3px right of centre — a play mark's
                visual centre is its centroid, not its box. */}
            <span className="flex size-16 items-center justify-center rounded-full bg-white/5 backdrop-blur-xl">
              <svg
                width={24}
                height={30}
                viewBox="0 0 24 30"
                className="translate-x-[3px]"
                aria-hidden
              >
                <path
                  d="M3 3 L21 15 L3 27 Z"
                  fill="white"
                  stroke="white"
                  strokeWidth={3}
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </button>
          <div className="absolute inset-x-0 bottom-0 flex flex-col gap-1 p-4 text-white">
            {review.href ? (
              <a
                href={review.href}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="hv3-rev-handle w-fit hover:underline"
              >
                {name}
              </a>
            ) : (
              <p className="hv3-rev-handle">{name}</p>
            )}
            {sub ? (
              <p className="hv3-rev-followers text-white/60">{sub}</p>
            ) : null}
          </div>
        </>
      )}
    </li>
  )
}

function TextCard({
  review,
  reviewsHref,
  onGrey,
}: {
  review: Extract<ReviewCard, { kind: "text" }>
  reviewsHref: string
  onGrey?: boolean
}) {
  // The card is the link: to the review where it lives (Google Maps,
  // Trustpilot) when the CMS carries that URL, else to our reviews page.
  const external = !!review.href
  return (
    <li className="h-[436px] w-[300px] shrink-0">
      <a
        href={review.href ?? reviewsHref}
        {...(external
          ? { target: "_blank", rel: "noopener noreferrer nofollow" }
          : {})}
        className={cn(
          "flex h-full flex-col rounded-2xl px-8 pt-[29px] pb-8 transition-colors",
          onGrey
            ? "bg-white hover:bg-neutral-50"
            : "bg-neutral-50 hover:bg-neutral-100"
        )}
      >
        <div className="flex min-h-0 flex-col gap-4">
          <p className="hv3-rev-headline text-neutral-900">
            &ldquo;{review.headline}&rdquo;
          </p>
          {review.body ? (
            /* The frame truncates Pavlov's body with an ellipsis at ~9 lines;
             the card is a fixed 436 and the copy is the reviewer's own. */
            <p className="type-body-xl line-clamp-[8] text-neutral-950">
              {review.body}
            </p>
          ) : null}
        </div>
        <figure className="mt-[26px] flex items-center gap-3">
          {review.avatar ? (
            <Image
              src={review.avatar}
              alt=""
              width={40}
              height={40}
              className="size-10 rounded-full object-cover"
            />
          ) : (
            <span className="size-10 rounded-full bg-neutral-200" aria-hidden />
          )}
          <figcaption>
            <p className="type-body-xl text-neutral-900">{review.author}</p>
            <div
              className="mt-1 flex"
              role="img"
              aria-label={`${review.rating} out of 5 stars`}
            >
              {Array.from({ length: review.rating }, (_, i) => (
                <StarIcon key={i} width={14} height={14} />
              ))}
            </div>
          </figcaption>
        </figure>
      </a>
    </li>
  )
}

export function ReviewsRail({
  section,
  className,
  eyebrow,
  heading,
  description,
  ctaLabel,
  ctaHref,
  followersSuffix,
  reviews,
  onGrey = false,
}: {
  /** Container class for the heading block and the CTA row. */
  section: string
  /** Band background and vertical padding. */
  className: string
  eyebrow?: string
  heading: string
  description?: string
  ctaLabel: string
  /** `/reviews` in the page's locale; also where a text card without its
   *  own source link goes. */
  ctaHref: string
  followersSuffix: string
  reviews: ReviewCard[]
  /** On the page grey (`className` sets it): the text cards, the reviews
   *  button and the arrows turn white so they stand out (Eugene, 07.10). */
  onGrey?: boolean
}) {
  const railRef = useRef<HTMLUListElement>(null)
  // One card and a gap per press — the frame's own step.
  const scrollBy = (dir: -1 | 1) =>
    railRef.current?.scrollBy({ left: dir * (300 + 14), behavior: "smooth" })

  return (
    <section className={className}>
      <ReviewsType />
      <div className={`${section} text-center`}>
        {eyebrow ? (
          <p className="type-label-lg mb-6 text-neutral-500">{eyebrow}</p>
        ) : null}
        <h2 className="type-display-md-emphasized mx-auto max-w-[664px] text-neutral-900">
          {heading}
        </h2>
        {description ? (
          <p className="type-body-lg mx-auto mt-4 max-w-2xl text-neutral-600">
            {description}
          </p>
        ) : null}
      </div>

      {/* Edge-to-edge rail: padding on the scroll container itself so the
          first card lines up with the content column and still scrolls. 72
          under the heading, not the frame's 43: the cards sat on the heading
          (Eugene, 11.09). */}
      {reviews.length > 0 && (
        <ul
          ref={railRef}
          className="mt-12 flex snap-x [scrollbar-width:none] gap-[14px] overflow-x-auto pr-6 pb-1 pl-6 md:mt-[72px] md:pl-[max(2.5rem,calc((100vw-1440px)/2+2.5rem))] [&::-webkit-scrollbar]:hidden"
        >
          {reviews.map((r) =>
            r.kind === "video" ? (
              <VideoCard
                key={r.id}
                review={r}
                followersSuffix={followersSuffix}
              />
            ) : (
              <TextCard
                key={r.id}
                review={r}
                reviewsHref={ctaHref}
                onGrey={onGrey}
              />
            )
          )}
        </ul>
      )}

      <div
        className={`${section} mt-6 flex items-center justify-between md:mt-[22px]`}
      >
        {/* The CTA is centred on the page; the arrows sit at the right edge.
            A spacer the arrows' width keeps the centring honest. */}
        <span className="hidden w-[88px] md:block" aria-hidden />
        <Link
          href={ctaHref}
          className={cn(
            buttonVariants({ variant: "outline" }),
            "px-8",
            onGrey &&
              "border-transparent bg-white hover:border-transparent hover:bg-neutral-50"
          )}
        >
          {ctaLabel}
        </Link>
        <div className="hidden gap-2 md:flex">
          <button
            type="button"
            onClick={() => scrollBy(-1)}
            aria-label="Previous reviews"
            className={cn(
              "flex size-10 cursor-pointer items-center justify-center rounded-full text-neutral-900 transition-colors",
              onGrey
                ? "bg-white hover:bg-neutral-50"
                : "bg-neutral-50 hover:bg-neutral-100"
            )}
          >
            <ChevronLeftIcon width={24} height={24} />
          </button>
          <button
            type="button"
            onClick={() => scrollBy(1)}
            aria-label="Next reviews"
            className={cn(
              "flex size-10 cursor-pointer items-center justify-center rounded-full text-neutral-900 transition-colors",
              onGrey
                ? "bg-white hover:bg-neutral-50"
                : "bg-neutral-100 hover:bg-neutral-200"
            )}
          >
            <ChevronRightIcon width={24} height={24} />
          </button>
        </div>
      </div>
    </section>
  )
}
